import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { fetchIdeas, fetchStats, fetchTopics, fmtRelative, fmtDateOnly } from "../lib/api";
import IdeaRow from "../components/IdeaRow";
import { ArrowRight, Sparkles } from "lucide-react";
import { useAuth } from "../lib/auth";

function Stat({ label, value, sub, testId }) {
  return (
    <div
      data-testid={testId}
      className="border border-[#27272A] bg-[#111111] p-3"
    >
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">
        {label}
      </div>
      <div className="text-2xl font-mono mt-1 tabnum">{value}</div>
      {sub && (
        <div className="text-[11px] text-zinc-500 font-mono mt-1">{sub}</div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const { data: stats } = useQuery({
    queryKey: ["stats"],
    queryFn: fetchStats,
    refetchInterval: 10000,
  });
  const { data: topics = [] } = useQuery({
    queryKey: ["topics"],
    queryFn: fetchTopics,
    refetchInterval: 15000,
  });
  const { data: ideas = [] } = useQuery({
    queryKey: ["ideas-top"],
    queryFn: () => fetchIdeas({ limit: 25, sort: "idea_rank" }),
    refetchInterval: 15000,
  });

  return (
    <div data-testid="dashboard-page" className="p-4 space-y-4">
      {user?.trial_active && (
        <div data-testid="trial-banner" className="border border-amber-500/50 bg-amber-500/10 p-3 flex items-center gap-3">
          <Sparkles size={14} className="text-amber-300" />
          <div className="text-[12px] text-amber-100 font-mono">
            <span className="uppercase tracking-wider">FREE_TRIAL_ACTIVE</span> · 5 MindStein usages included ·
            ends {fmtDateOnly(user.trial_ends_at)}
          </div>
          <Link to="/account" className="ml-auto h-8 px-3 border border-amber-400 bg-amber-400 text-black hover:bg-amber-300 text-[10px] font-mono uppercase tracking-wider flex items-center">
            CHOOSE_A_PLAN →
          </Link>
        </div>
      )}
      <div>
        <h1 className="font-mono tracking-tight text-2xl">
        OVERVIEW
      </h1>
      <p className="text-zinc-500 text-[12px] mt-1">
        Daily topic ingestion & 30-day retention. Continuous top-up to {stats?.target_today || 100}/day across {stats?.total_topics || 10} libraries.
      </p>
      </div>

      {/* HOW IT WORKS — 4-step user journey */}
      <section
        data-testid="how-it-works"
        className="border border-[#27272A] bg-gradient-to-br from-[#0E0E0E] to-[#0A0A0A] grid grid-cols-1 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-[#27272A]"
      >
        {[
          {
            num: "01",
            label: "BROWSE",
            color: "text-emerald-300",
            title: "Open a topic library",
            body: "Pick any of the 10 libraries (Technology, Science, Food & Health…). Each is topped up to 10 fresh, ranked ideas every day.",
            cta: "/topics/technology",
            ctaLabel: "TRY_TECHNOLOGY",
          },
          {
            num: "02",
            label: "DISCOVER",
            color: "text-purple-300",
            title: "Scout innovation signals",
            body: "Pro & Elite plans unlock the Innovation feed — up to 50 frontier R&D signals every day, capped at 500 live items with 30-day retention.",
            cta: "/innovation",
            ctaLabel: "OPEN_INNOVATION",
          },
          {
            num: "03",
            label: "REMIX",
            color: "text-amber-300",
            title: "Ask the MindStein Bot",
            body: "Combine, compare, expand, rename, prioritize or validate any items with the bot. Outputs are structured & copy-ready.",
            cta: "/bot",
            ctaLabel: "OPEN_BOT",
          },
          {
            num: "04",
            label: "KEEP",
            color: "text-rose-300",
            title: "Save to your library",
            body: "Tap the bookmark on any idea, innovation signal, or bot answer. Your Personal Library survives 30-day expiry & rotation.",
            cta: "/library",
            ctaLabel: "VIEW_MY_LIB",
          },
          {
            num: "05",
            label: "PROTECT",
            color: "text-amber-300",
            title: "OneStamp your work",
            body: "Every MindStein bot answer is auto-hashed, timestamped, and signed — a portable prior-art proof you can download as a PDF or delete anytime. RFC 3161 + Ed25519 upgrade before launch.",
            cta: "/onestamp",
            ctaLabel: "OPEN_ONESTAMP",
          },
        ].map((s) => (
          <div key={s.num} className="p-4 flex flex-col">
            <div className={"text-[10px] uppercase tracking-[0.2em] font-mono " + s.color}>
              STEP_{s.num} · {s.label}
            </div>
            <div className="mt-2 font-mono text-base text-white">{s.title}</div>
            <p className="mt-1 text-[12px] text-zinc-400 leading-relaxed flex-1">
              {s.body}
            </p>
            <Link
              to={s.cta}
              data-testid={`how-step-cta-${s.num}`}
              className="mt-3 inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-zinc-300 hover:text-white"
            >
              {s.ctaLabel} <ArrowRight size={11} />
            </Link>
          </div>
        ))}
      </section>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <Stat
          testId="stat-active-ideas"
          label="ACTIVE_IDEAS"
          value={stats?.total_ideas_active ?? "—"}
          sub={`${stats?.total_ideas_archived ?? 0} archived/expired`}
        />
        <Stat
          testId="stat-ideas-today"
          label="IDEAS_TODAY"
          value={stats?.ideas_today ?? "—"}
          sub={`target ${stats?.target_today ?? 100}`}
        />
        <Stat
          testId="stat-sources"
          label="SOURCES"
          value={stats?.total_sources ?? "—"}
          sub={`${stats?.sources_verified ?? 0} verified`}
        />
        <Stat
          testId="stat-runs"
          label="RUNS_TODAY"
          value={stats?.runs_today ?? "—"}
          sub={`${stats?.runs_total ?? 0} total`}
        />
        <Stat
          testId="stat-last-run"
          label="LAST_RUN"
          value={fmtRelative(stats?.last_run_at)}
          sub="scheduler · live"
        />
      </div>

      {/* topic matrix */}
      <section
        data-testid="topic-matrix"
        className="border border-[#27272A] bg-[#0A0A0A]"
      >
        <div className="px-3 py-2 border-b border-[#27272A] flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">
            TOPIC_MATRIX · DAILY_PROGRESS
          </span>
          <span className="text-[10px] text-zinc-600 font-mono">
            target 10 / topic / day
          </span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5">
          {topics.map((t) => {
            const filled = t.today_count >= t.today_target;
            const pct = Math.min(100, (t.today_count / t.today_target) * 100);
            return (
              <Link
                key={t.id}
                to={`/topics/${t.slug}`}
                data-testid={`topic-tile-${t.slug}`}
                className="p-3 border-r border-b border-[#161616] hover:bg-[#111111] group"
              >
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-mono uppercase tracking-wider truncate">
                    {t.name}
                  </span>
                  <ArrowRight
                    size={12}
                    className="text-zinc-700 group-hover:text-white"
                  />
                </div>
                <div className="mt-2 flex items-baseline gap-2">
                  <span
                    className={
                      "text-2xl font-mono tabnum " +
                      (filled ? "text-emerald-400" : "text-amber-400")
                    }
                  >
                    {t.today_count}
                  </span>
                  <span className="text-zinc-500 font-mono text-[12px]">
                    / {t.today_target}
                  </span>
                </div>
                <div className="mt-1 h-[3px] bg-[#1a1a1a]">
                  <div
                    style={{
                      width: `${pct}%`,
                      height: "100%",
                      background: filled ? "#10B981" : "#F59E0B",
                    }}
                  />
                </div>
                <div className="mt-1 text-[10px] text-zinc-500 font-mono">
                  {t.total_active} active · {fmtRelative(t.last_run_at)}
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* idea feed */}
      <section
        data-testid="top-ideas-section"
        className="border border-[#27272A] bg-[#0A0A0A]"
      >
        <div className="px-3 py-2 border-b border-[#27272A] flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">
            TOP_IDEAS · RANKED
          </span>
          <Link
            to="/runs"
            data-testid="see-runs-link"
            className="text-[10px] font-mono uppercase text-zinc-500 hover:text-white"
          >
            VIEW_RUNS →
          </Link>
        </div>
        {ideas.length === 0 ? (
          <div className="grid-bg p-10 text-center">
            <div className="text-[12px] text-zinc-500 font-mono uppercase tracking-wider">
              NO_IDEAS_YET · trigger RUN_ALL or wait for scheduler
            </div>
          </div>
        ) : (
          ideas.map((it, i) => <IdeaRow key={it.id} idea={it} index={i} />)
        )}
      </section>
    </div>
  );
}
